import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Trophy, ChevronRight, ChevronLeft, MapPin } from 'lucide-react';

const OnboardingPage = () => {
    const [step, setStep] = useState(1);
    const [role, setRole] = useState('');
    const [position, setPosition] = useState('');
    const [region, setRegion] = useState('');
    const [bio, setBio] = useState('');
    const [loading, setLoading] = useState(false);
    const { completeOnboarding, user } = useAuth();
    const navigate = useNavigate();

    const positions = ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'];
    const regions = ['Riyadh', 'Jeddah', 'Dammam', 'Mecca', 'Medina', 'Other'];

    const handleComplete = async () => {
        if (!role) {
            toast.error('Please select a role');
            return;
        }

        setLoading(true);
        try {
            await completeOnboarding({
                role,
                position: role === 'player' ? position : null,
                region,
                bio
            });
            toast.success('Welcome to OneGame!');
            navigate('/');
        } catch (error) {
            console.error('Onboarding error:', error);
            toast.error('Failed to complete setup');
        } finally {
            setLoading(false);
        }
    };

    const canProceed = () => {
        if (step === 1) return !!role;
        if (step === 2 && role === 'player') return !!position;
        return true;
    };

    const nextStep = () => {
        if (step === 1 && role === 'fan') {
            setStep(3); // Skip position step for fans
        } else {
            setStep(step + 1);
        }
    };

    const prevStep = () => {
        if (step === 3 && role === 'fan') {
            setStep(1);
        } else {
            setStep(step - 1);
        }
    };

    return (
        <div className="min-h-screen bg-black flex flex-col" data-testid="onboarding-page">
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                {/* Progress Indicator */}
                <div className="flex items-center gap-2 mb-8">
                    {[1, 2, 3].map((s) => (
                        <div
                            key={s}
                            className={`w-12 h-1 rounded-full transition-colors ${
                                s <= step ? 'bg-primary' : 'bg-white/20'
                            }`}
                        />
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    {/* Step 1: Role Selection */}
                    {step === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="w-full max-w-sm"
                        >
                            <h1 className="font-heading text-3xl font-bold text-white text-center mb-2">
                                Welcome, {user?.username}!
                            </h1>
                            <p className="text-white/60 text-center mb-8">
                                What best describes you?
                            </p>

                            <div className="space-y-4">
                                <button
                                    onClick={() => setRole('player')}
                                    className={`w-full p-6 rounded-2xl border-2 transition-all ${
                                        role === 'player'
                                            ? 'border-primary bg-primary/10'
                                            : 'border-border bg-surface hover:border-white/30'
                                    }`}
                                    data-testid="role-player-button"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                                            role === 'player' ? 'bg-primary' : 'bg-white/10'
                                        }`}>
                                            <Trophy className="w-7 h-7 text-white" />
                                        </div>
                                        <div className="text-left">
                                            <h3 className="text-white font-semibold text-lg">Player</h3>
                                            <p className="text-white/60 text-sm">Amateur, Semi-Pro, or Professional</p>
                                        </div>
                                    </div>
                                </button>

                                <button
                                    onClick={() => setRole('fan')}
                                    className={`w-full p-6 rounded-2xl border-2 transition-all ${
                                        role === 'fan'
                                            ? 'border-primary bg-primary/10'
                                            : 'border-border bg-surface hover:border-white/30'
                                    }`}
                                    data-testid="role-fan-button"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                                            role === 'fan' ? 'bg-primary' : 'bg-white/10'
                                        }`}>
                                            <User className="w-7 h-7 text-white" />
                                        </div>
                                        <div className="text-left">
                                            <h3 className="text-white font-semibold text-lg">Fan</h3>
                                            <p className="text-white/60 text-sm">Follow matches and support teams</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 2: Position (Players Only) */}
                    {step === 2 && role === 'player' && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="w-full max-w-sm"
                        >
                            <h1 className="font-heading text-3xl font-bold text-white text-center mb-2">
                                What's your position?
                            </h1>
                            <p className="text-white/60 text-center mb-8">
                                Select where you play best
                            </p>

                            <div className="grid grid-cols-2 gap-3">
                                {positions.map((pos) => (
                                    <button
                                        key={pos}
                                        onClick={() => setPosition(pos)}
                                        className={`p-4 rounded-xl border-2 transition-all ${
                                            position === pos
                                                ? 'border-primary bg-primary/10'
                                                : 'border-border bg-surface hover:border-white/30'
                                        }`}
                                        data-testid={`position-${pos.toLowerCase()}-button`}
                                    >
                                        <span className="text-white font-medium">{pos}</span>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* Step 3: Region & Bio */}
                    {step === 3 && (
                        <motion.div
                            key="step3"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="w-full max-w-sm"
                        >
                            <h1 className="font-heading text-3xl font-bold text-white text-center mb-2">
                                Almost there!
                            </h1>
                            <p className="text-white/60 text-center mb-8">
                                Tell us a bit more about yourself
                            </p>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-white/80 flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        Region
                                    </Label>
                                    <Select value={region} onValueChange={setRegion}>
                                        <SelectTrigger className="bg-surface border-border text-white h-12" data-testid="region-select">
                                            <SelectValue placeholder="Select your region" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-card-bg border-border">
                                            {regions.map((r) => (
                                                <SelectItem key={r} value={r} className="text-white hover:bg-primary">
                                                    {r}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-white/80">Bio (Optional)</Label>
                                    <Textarea
                                        value={bio}
                                        onChange={(e) => setBio(e.target.value)}
                                        placeholder="Tell others about yourself..."
                                        className="bg-surface border-border text-white placeholder:text-white/40 min-h-[100px] resize-none"
                                        data-testid="bio-input"
                                    />
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="w-full max-w-sm mt-8 flex gap-3">
                    {step > 1 && (
                        <Button
                            onClick={prevStep}
                            variant="outline"
                            className="flex-1 h-12 border-border bg-surface text-white hover:bg-white/10"
                            data-testid="onboarding-back-button"
                        >
                            <ChevronLeft className="w-5 h-5 mr-1" />
                            Back
                        </Button>
                    )}
                    
                    {step < 3 ? (
                        <Button
                            onClick={nextStep}
                            disabled={!canProceed()}
                            className="flex-1 bg-primary hover:bg-primary-hover text-white font-semibold h-12 rounded-full"
                            data-testid="onboarding-next-button"
                        >
                            Continue
                            <ChevronRight className="w-5 h-5 ml-1" />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleComplete}
                            disabled={loading}
                            className="flex-1 bg-primary hover:bg-primary-hover text-white font-semibold h-12 rounded-full"
                            data-testid="onboarding-complete-button"
                        >
                            {loading ? 'Setting up...' : 'Get Started'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingPage;
